import { FC, useState, useMemo } from 'react'
import { Copy, Check } from 'lucide-react'
import { Proto } from 'types/demo3'

interface Props {
  data: Proto.SessionReportResponse | null
}

// Custom JSON syntax highlighter
const JsonSyntaxHighlight: FC<{ json: any }> = ({ json }) => {
  const formatJson = (obj: any, indent: number = 0): any => {
    if (obj === null) {
      return <span className='text-gray-400'>null</span>
    }

    if (obj === undefined) {
      return <span className='text-gray-400'>undefined</span>
    }

    if (typeof obj === 'boolean') {
      return <span className='text-purple-400'>{obj.toString()}</span>
    }

    if (typeof obj === 'number') {
      return <span className='text-blue-400'>{obj}</span>
    }

    if (typeof obj === 'string') {
      return <span className='text-green-400'>"{obj}"</span>
    }

    const spaces = '  '.repeat(indent)
    const nextIndent = indent + 1
    const nextSpaces = '  '.repeat(nextIndent)

    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        return <span className='text-gray-300'>[]</span>
      }

      return (
        <span>
          <span className='text-gray-300'>[</span>
          {obj.map((item, index) => (
            <div key={index}>
              <span>{nextSpaces}</span>
              {formatJson(item, nextIndent)}
              {index < obj.length - 1 && (
                <span className='text-gray-300'>,</span>
              )}
            </div>
          ))}
          <div>
            <span>{spaces}</span>
            <span className='text-gray-300'>]</span>
          </div>
        </span>
      )
    }

    if (typeof obj === 'object') {
      const entries = Object.entries(obj)
      if (entries.length === 0) {
        return <span className='text-gray-300'>{'{}'}</span>
      }

      return (
        <span>
          <span className='text-gray-300'>{'{'}</span>
          {entries.map(([key, value], index) => (
            <div key={key}>
              <span>{nextSpaces}</span>
              <span className='text-orange-400'>"{key}"</span>
              <span className='text-gray-300'>: </span>
              {formatJson(value, nextIndent)}
              {index < entries.length - 1 && (
                <span className='text-gray-300'>,</span>
              )}
            </div>
          ))}
          <div>
            <span>{spaces}</span>
            <span className='text-gray-300'>{'}'}</span>
          </div>
        </span>
      )
    }

    return <span>{JSON.stringify(obj)}</span>
  }

  return <div className='font-mono text-sm'>{formatJson(json)}</div>
}

const JsonView: FC<Props> = ({ data }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!data) return

    const jsonText = JSON.stringify(data, null, 2)

    // Try modern clipboard API first (will fail in restricted environments)
    try {
      await navigator.clipboard.writeText(jsonText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      return
    } catch (err) {
      // Expected to fail in some environments, continue to fallback
    }

    // Fallback: Create temporary textarea and use execCommand
    const textArea = document.createElement('textarea')
    textArea.value = jsonText
    textArea.style.position = 'fixed'
    textArea.style.top = '-9999px'
    textArea.style.left = '-9999px'
    textArea.style.width = '2em'
    textArea.style.height = '2em'
    textArea.style.padding = '0'
    textArea.style.border = 'none'
    textArea.style.outline = 'none'
    textArea.style.boxShadow = 'none'
    textArea.style.background = 'transparent'

    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      const successful = document.execCommand('copy')
      if (successful) {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } else {
        console.warn('Copy command returned false')
      }
    } catch (fallbackErr) {
      console.error('Both copy methods failed:', fallbackErr)
    } finally {
      document.body.removeChild(textArea)
    }
  }

  const jsonContent = useMemo(() => {
    if (!data) return null
    return <JsonSyntaxHighlight json={data} />
  }, [data])

  return (
    <div className='bg-gray-900 rounded-lg relative'>
      {/* Copy button positioned inside the dark container */}
      <button
        onClick={handleCopy}
        className='absolute top-4 right-4 z-10 flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-md transition-colors text-gray-100'
      >
        {copied ? (
          <>
            <Check size={16} className='text-green-400' />
            <span className='text-green-400 text-sm'>Copied!</span>
          </>
        ) : (
          <>
            <Copy size={16} />
            <span className='text-sm'>Copy JSON</span>
          </>
        )}
      </button>

      {/* JSON content with custom syntax highlighting */}
      <div
        className='p-6 pt-14 overflow-auto'
        style={{
          backgroundColor: '#1a1a1a',
          maxHeight: 'calc(100vh - 12rem)',
          minHeight: 'calc(100vh - 14rem)'
        }}
      >
        {data ? jsonContent : <div className='text-gray-400'>Loading...</div>}
      </div>
    </div>
  )
}

export default JsonView
